import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Paper,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  anchorPosition: { x: number; y: number };
  pointLabel: string;
  initialLatitude?: number;
  initialLongitude?: number;
  onSave: (latitude: number, longitude: number) => void;
  onClose: () => void;
  onDelete?: () => void;
};

export const GPSEntryPopover: React.FC<Props> = ({
  open,
  anchorPosition,
  pointLabel,
  initialLatitude,
  initialLongitude,
  onSave,
  onClose,
  onDelete,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [latitude, setLatitude] = React.useState(
    initialLatitude?.toString() ?? ""
  );
  const [longitude, setLongitude] = React.useState(
    initialLongitude?.toString() ?? ""
  );
  const [error, setError] = React.useState("");

  const latInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setTimeout(() => latInputRef.current?.focus(), 100);
    }
  }, [open]);

  React.useEffect(() => {
    setLatitude(initialLatitude?.toString() ?? "");
    setLongitude(initialLongitude?.toString() ?? "");
    setError("");
  }, [initialLatitude, initialLongitude, open]);

  const validate = (): boolean => {
    // Check for empty values
    if (!latitude.trim() || !longitude.trim()) {
      setError(t("settings.rampsSettings.setupWizard.gps.emptyError", "Both coordinates required"));
      return false;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      setError(t("settings.rampsSettings.setupWizard.gps.latError", "Latitude must be -90 to 90"));
      return false;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setError(t("settings.rampsSettings.setupWizard.gps.lngError", "Longitude must be -180 to 180"));
      return false;
    }
    setError("");
    return true;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(parseFloat(latitude), parseFloat(longitude));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Handle paste from Google Maps format: "39.64805, -106.51903"
  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").trim();
    // Match Google Maps format: lat, lng (with optional space after comma)
    const match = pasted.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (match) {
      e.preventDefault();
      setLatitude(match[1]);
      setLongitude(match[2]);
      setError("");
    }
  };

  const isValid = latitude.trim() && longitude.trim();

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={{ top: anchorPosition.y, left: anchorPosition.x }}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: 3,
          },
        },
      }}
    >
      <Paper sx={{ p: 2.5, width: 300 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.primary.main,
                color: "white",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {pointLabel}
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("settings.rampsSettings.setupWizard.gps.title", "Point {{label}} GPS", { label: pointLabel })}
            </Typography>
          </Box>

          {/* Inputs */}
          <TextField
            inputRef={latInputRef}
            label={t("settings.rampsSettings.setupWizard.gps.latitude", "Latitude")}
            placeholder="40.7128 or paste from Google Maps"
            value={latitude}
            onChange={(e) => {
              setLatitude(e.target.value);
              setError("");
            }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            size="medium"
            fullWidth
          />

          <TextField
            label={t("settings.rampsSettings.setupWizard.gps.longitude", "Longitude")}
            placeholder="-74.0060"
            value={longitude}
            onChange={(e) => {
              setLongitude(e.target.value);
              setError("");
            }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            size="medium"
            fullWidth
          />

          {/* Error message */}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, pt: 1 }}>
            {onDelete ? (
              <Button
                onClick={onDelete}
                color="error"
                startIcon={<DeleteOutlineIcon />}
              >
                {t("common.delete", "Delete")}
              </Button>
            ) : (
              <Box />
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isValid}
                endIcon={<CheckIcon />}
              >
                {t("common.save", "Save")}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Popover>
  );
};
